import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import IdeaCard from "@/components/ideas/IdeaCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import NewIdeaDialog from "@/components/ideas/NewIdeaDialog";

interface MyIdeasTabProps {
  userId: string;
  onCountChange?: (count: number) => void;
  /** Fires when the user toggles support on one of their own ideas, so the parent can refresh the supporting count. */
  onSupportChange?: () => void;
}

const MyIdeasTab = ({ userId, onCountChange, onSupportChange }: MyIdeasTabProps) => {
  const [ideas, setIdeas] = useState<any[] | null>(null);
  const [votedIds, setVotedIds] = useState<Set<string>>(new Set());
  const [editingIdea, setEditingIdea] = useState<any | null>(null);

  const load = useCallback(async () => {
    const { data: ideasData } = await supabase
      .from("ideas")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    const list = ideasData ?? [];

    // Attach author profile (always the same user here)
    const { data: profile } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", userId)
      .maybeSingle();
    const enriched = list.map((i) => ({ ...i, profiles: profile }));

    const ids = list.map((i) => i.id);
    if (ids.length > 0) {
      const { data: votes } = await supabase
        .from("idea_votes")
        .select("idea_id")
        .eq("user_id", userId)
        .in("idea_id", ids);
      setVotedIds(new Set((votes ?? []).map((v) => v.idea_id)));
    } else {
      setVotedIds(new Set());
    }

    setIdeas(enriched);
    onCountChange?.(list.length);
  }, [userId, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVote = async (ideaId: string, hasVoted: boolean) => {
    if (hasVoted) {
      const { error } = await supabase
        .from("idea_votes")
        .delete()
        .eq("user_id", userId)
        .eq("idea_id", ideaId);
      if (error) {
        toast.error("Failed to remove vote");
        return;
      }
      setVotedIds((prev) => {
        const next = new Set(prev);
        next.delete(ideaId);
        return next;
      });
      setIdeas((prev) =>
        prev?.map((i) => (i.id === ideaId ? { ...i, vote_count: Math.max(0, i.vote_count - 1) } : i)) ?? null,
      );
      onSupportChange?.();
    } else {
      const { error } = await supabase.from("idea_votes").insert({ user_id: userId, idea_id: ideaId });
      if (error) {
        toast.error("Failed to add vote");
        return;
      }
      setVotedIds((prev) => new Set(prev).add(ideaId));
      setIdeas((prev) =>
        prev?.map((i) => (i.id === ideaId ? { ...i, vote_count: i.vote_count + 1 } : i)) ?? null,
      );
      onSupportChange?.();
    }
  };

  if (ideas === null) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-64 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  if (ideas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border p-10 text-center">
        <p className="text-sm text-muted-foreground">You haven't posted any projects yet.</p>
        <Link to="/ideas">
          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
            Share your first project
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {ideas.map((idea) => (
        <div key={idea.id} className="flex flex-col">
          <IdeaCard
            idea={idea}
            hasVoted={votedIds.has(idea.id)}
            onVote={(hasVoted) => handleVote(idea.id, hasVoted)}
            currentUserId={userId}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditingIdea(idea)}
            className="gap-2 self-start -mt-2"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
        </div>
      ))}
      {editingIdea && (
        <NewIdeaDialog
          open={!!editingIdea}
          onOpenChange={(o) => !o && setEditingIdea(null)}
          idea={{
            id: editingIdea.id,
            title: editingIdea.title,
            description: editingIdea.description,
            category: editingIdea.category,
            tags: editingIdea.tags,
            image_url: editingIdea.image_url,
          }}
          onSaved={() => {
            setEditingIdea(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default MyIdeasTab;
